/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type { HasRequiredKeys } from 'type-fest';

import { buildUseQuery } from '@main/core-ui';
import type {
  PrivacyCenterModule,
  PrivacyCenterModuleContextMap,
} from '@main/pc-types';
import { PRIVACY_CENTER_MODULE_CONTEXT_MAP } from '@main/pc-types';
import type {
  ParamsToType,
  QueryEndpoint,
  ResponseToType,
} from '@main/schema-utils';

import { useStaticPrivacyCenterModule } from './useStaticPrivacyCenterModule';

/**
 * Whether the query is from CDN or dynamic backend
 */
export type CDNQuerySource = 'cdn' | 'dynamic';

/**
 * Options for the hook that builds a query with dynamic fallback
 */
type MakeHookOptions<Variables extends object> =
  HasRequiredKeys<Variables> extends true
    ? // Required argument if some variables are required
      [
        options: {
          /** Locale */
          locale: string | undefined;
          /** Query variables */
          variables: Variables;
        },
      ]
    : // Optional argument when no variables are required
      [
        options?: {
          /** Locale */
          locale?: string;
          /** Query variables */
          variables?: Variables;
        },
      ];

/**
 * Call CDN with dynamic fallback to dynamic module
 *
 * @param moduleName - The privacy center module name to query
 * @returns Hook for querying the CDN with dynamic fallback
 */
export function buildUseCdnQueryWithFallback<
  Module extends PrivacyCenterModule,
  Endpoint extends PrivacyCenterModuleContextMap[Module]['endpoint'],
  Result extends ResponseToType<Endpoint['response']>,
  UseQueryEndpoint extends QueryEndpoint<
    Endpoint['name'],
    Endpoint['params'],
    Endpoint['response']
  >,
  Variables extends ParamsToType<Endpoint['params']>,
  HookOptions extends MakeHookOptions<Variables>,
>(moduleName: Module) {
  // Get endpoint based on module name
  const { endpoint } = PRIVACY_CENTER_MODULE_CONTEXT_MAP[moduleName];

  // Build query hook
  const useQuery = buildUseQuery(endpoint as UseQueryEndpoint);

  // Build hook that uses CDN with dynamic fallback
  const useCDNQueryWithDynamicFallback = (...[options = {}]: HookOptions) => {
    const { locale, variables } = options;

    // Load data from CDN
    const cdnQuery = useStaticPrivacyCenterModule<Result>(
      endpoint.name,
      locale,
    );
    const { skipGql: skip } = cdnQuery;

    // Load data from API if CDN query is skipped
    const dynamicQuery = useQuery({
      skip,
      variables,
    });

    return {
      data: skip ? cdnQuery.directData : dynamicQuery.data,
      loading: skip ? cdnQuery.directLoading : dynamicQuery.loading,
      error: skip ? cdnQuery.directError : dynamicQuery.error,
      source: (skip ? 'cdn' : 'dynamic') as CDNQuerySource,
      cdnQuery,
      dynamicQuery,
    };
  };

  return useCDNQueryWithDynamicFallback;
}

/* eslint-enable @typescript-eslint/explicit-function-return-type */
